import { ApiFailure, NetworkFailure } from '../../api/client';
import type { MessageKey, T } from '../../i18n';
import type { ErrorCode } from '../../../shared/errors';

/** "3,4 MB" / "820 kB" in the UI locale (decimal units, like the size limits). */
export function formatBytes(bytes: number, loc: string): string {
  const nf = (max: number) => new Intl.NumberFormat(loc, { maximumFractionDigits: max });
  if (bytes >= 1_000_000) return `${nf(bytes >= 100_000_000 ? 0 : 1).format(bytes / 1_000_000)} MB`;
  if (bytes >= 1000) return `${nf(0).format(bytes / 1000)} kB`;
  return `${bytes} B`;
}

/** 83 000 ms → "1:23" */
export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Composer · arranger line for song rows. */
export function creditsLine(t: T, song: { composer: string | null; arranger: string | null }): string {
  const parts: string[] = [];
  if (song.composer) parts.push(song.composer);
  if (song.arranger) parts.push(t('library.arranger_short', { name: song.arranger }));
  return parts.join(' · ');
}

export function errorCodeText(t: T, code: ErrorCode): string {
  return t(`error.${code}` as MessageKey);
}

/** Human text for any thrown error (API code, network, or unknown). */
export function errorText(t: T, e: unknown): string {
  if (e instanceof ApiFailure) return errorCodeText(t, e.code);
  if (e instanceof NetworkFailure) return t('error.network');
  return t('common.error');
}
